import type { Translations } from "./sl";
import { FAQ_DE } from "@/content/faqData";
import { legalDe } from "./legal-de";
import { propertyChecksDe } from "./property-checks-de";
import { supportingDocumentsDe } from "./supporting-documents";

export const de: Translations = {
  common: {
    error: "Fehler",
    cancel: "Abbrechen",
    send: "Senden",
    sending: "Wird gesendet...",
    or: "Oder",
    and: "und",
    aptNo: "Nr.",
    aptNoTitle: "Wohnungsnummer",
    toggleMenu: "Menü umschalten",
    skipToContent: "Zum Inhalt springen",
    language: "Sprache",
  },
  announcement: {
    text: "Kostenlos testen — eine Vorschau für Ihre Adresse.",
    cta: "Jetzt starten",
    dismiss: "Hinweis schließen",
  },
  header: {
    features: "Funktionen",
    howItWorks: "So funktioniert's",
    pricing: "Preise",
    dashboard: "Dashboard",
    adminDashboard: "Admin-Dashboard",
    signOut: "Abmelden",
    login: "Anmelden",
    register: "Registrieren",
  },
  brand: {
    "startLabel": "Starten Sie mit der Immobilienadresse",
    "searchCta": "Weiter",
    "freeNote": "Kostenlose Vorschau nach E-Mail-Bestätigung.",
    "sampleLabel": "Musterbericht",
    "sampleNote": "Fiktive Daten · Inhaltsvorschau",
    "sampleType": "Wohnung · Ljubljana",
    "estimate": "Informative Wertschätzung",
    "uncertainty": "Schätzspanne · Darstellung der Unsicherheit",
    "sources": "Grundlagen der Schätzung",
    "sourceSales": "Abgeschlossene Verkäufe",
    "sourceRegistry": "GURS-Register",
    "sourceListings": "Vergleichbare Angebote",
    "checks": "Noch zu prüfen",
    "checkTitle": "Grundbuchstand",
    "checkNote": "Eigentum und Belastungen vor der Unterschrift prüfen.",
    "sourceTitle": "Eine fundierte Grundlage für Ihre Entscheidung.",
    "sourceDescription": "Öffentliche Register und Marktdaten verständlich zusammengeführt. Die Verfügbarkeit hängt von der Immobilie ab.",
    "sourceGurs": "Gebäude-, Einheits- und Grundstücksdaten",
    "sourceEtn": "Erfasste Verkäufe und Preisentwicklung",
    "sourceZk": "Eigentum und Belastungen bei vorliegendem Auszug",
    "sourceAds": "Einordnung aktueller Angebotspreise",
    "sampleCta": "Sehen Sie, was der Bericht enthält",
    "sampleDisclaimer": "Darstellung mit fiktiven Daten, keine Analyse dieser Adresse.",
    "featuresEyebrow": "Im Bericht",
    "featuresTitle": "Von Daten zu einem klareren Bild.",
    "featuresDescription": "Sehen Sie die Schätzung mit ihren Grundlagen und die Fragen, die noch offen sind.",
    "processTitle": "Von der Adresse zum nächsten Schritt.",
    "illustration": "Veranschaulichung des Unterschieds",
    "included": "In jedem vollständigen Bericht",
    "previewEyebrow": "Ein genauerer Blick",
    "previewTitle": "Ein Bericht, mit dem Sie arbeiten können.",
    "previewDescription": "Speichern, mit Ihrem Partner teilen oder zu einer Fachperson mitnehmen. Fakten, Grundlagen und nächste Schritte bleiben zusammen.",
    "previewPoints": [
      "Klare Schätzung mit ihrer Unsicherheit",
      "Nachvollziehbare Grundlagen und Vergleichsverkäufe",
      "Offene Risiken und Prüfungen vor der Unterschrift"
    ],
    "previewLanguage": "Der Musterbericht ist auf Slowenisch.",
    "featureItems": [
      {
        "title": "GURS-Immobiliendaten",
        "description": "Flächen, Baujahr, Gebäude- und Grundstücksdaten sowie der verallgemeinerte Wert."
      },
      {
        "title": "Abgeschlossene Verkäufe",
        "description": "Vergleichbare erfasste Verkäufe in der Nähe mit Preisen und Zeitpunkten."
      },
      {
        "title": "Lage und Umgebung",
        "description": "Standort, Erreichbarkeit und Lagefaktoren der Immobilie."
      },
      {
        "title": "Eine erklärte Schätzung",
        "description": "Eine informative Wertschätzung mit Unsicherheitsspanne und ihren Grundlagen."
      },
      {
        "title": "Risiken und nächste Schritte",
        "description": "Klar markierte offene Prüfungen und Fakten für das Preisgespräch."
      },
      {
        "title": "Ein übersichtliches PDF",
        "description": "Ein Bericht zum Speichern, Teilen oder für das Gespräch mit einer Fachperson."
      }
    ]
  },
  hero: {
    badgeGurs: "Offizielle GURS-Daten",
    badgeValidity: "Credits 1 Jahr gültig",
    badgeReports: "100+ erstellte Berichte",
    eyebrow: "Immobilienberichte für Slowenien",
    titleLead: "Erst die Fakten.",
    titleHighlight: "Dann entscheiden.",
    subtitle:
      "Verstehen Sie den Preis, vergleichen Sie abgeschlossene Verkäufe und erkennen Sie offene Fragen. Klar in einem Bericht zusammengefasst.",
    tagline: "Ein unabhängiger Blick auf öffentliche Register und Marktdaten.",
    featureOfficialTitle: "Offizielle Daten",
    featureOfficialDesc:
      "Direkter Zugriff auf die neuesten offiziellen ETN-, GURS- und Grundbuchdaten.",
    featureMarketTitle: "Marktanalyse & Vergleiche",
    featureMarketDesc:
      "Vergleich mit vergangenen Verkäufen und aktuellen Inseraten in der Umgebung.",
    featureAiTitle: "Argumente für die Verhandlung",
    featureAiDesc:
      "Ein PDF mit Anleitung, wie Sie einen niedrigeren Preis verhandeln und welche Risiken Sie vor der Unterzeichnung prüfen sollten.",
    ctaOrder: "Ausführlichen Bericht bestellen",
    ctaPreview: "Muster ansehen",
  },
  propertyNextStep: {
    "eyebrow": "Ausgewählte Immobilie",
    "edit": "Adresse ändern",
    "previewTitle": "Mit einer kostenlosen Vorschau starten",
    "previewNote": "Sehen Sie GURS-Immobiliendaten und eine Vorschau der Ergebnisse zu dieser Adresse. Unsere Wertschätzung und die vollständige Analyse schalten Sie mit einem bezahlten Credit frei.",
    "noCard": "Eine Vorschau nach E-Mail-Bestätigung · keine Karte nötig",
    "signedInTitle": "Mit dieser Immobilie fortfahren",
    "signedInNote": "Ihre Adresse steht im Dashboard bereit. Nutzen Sie dort Ihre verfügbaren Credits für eine Vorschau oder einen vollständigen Bericht.",
    "fullTitle": "Der vollständige Bericht zu dieser Adresse",
    "fullNote": "Wertschätzung mit Spanne, vergleichbare Verkäufe, Risiken und nächste Schritte. Der Umfang hängt von verfügbaren Daten ab.",
    "fullCta": "Vollständiger Bericht · {{price}}",
    "packagesNote": "1 Credit für diese Immobilie · Pakete für mehrere Adressen unten"
  },
  addressSearch: {
    incompleteAddress: "Geben Sie einen Straßen- oder Ortsnamen und eine Hausnummer ein.",
    addressLabel: "Adresse der Immobilie",
    propertyTypeLabel: "Immobilientyp",
    house: "Haus",
    apartment: "Wohnung",
    placeholder: "Adresse eingeben (z. B. Trubarjeva 10, Ljubljana)",
    ordering: "Wird bestellt...",
    getReport: "Kostenlosen Bericht erhalten",
    hintHouse: "Geben Sie die Adresse des Hauses ein, das Sie interessiert, für eine Wertschätzung",
    hintApartment: "Geben Sie die Adresse der Wohnung ein, die Sie interessiert, für eine Wertschätzung",
    hintApartmentNo: " • Wohnungsnummer hinzufügen",
    toastEnterAddress: "Adresse eingeben",
    toastEnterAddressDesc: "Bitte geben Sie die Adresse der Immobilie ein.",
    toastEnterApt: "Wohnungsnummer eingeben",
    toastEnterAptDesc: "Bitte geben Sie die Wohnungsnummer ein.",
    suggestionsStatus: "{{count}} Adressvorschläge. Mit den Pfeiltasten wählen und mit Enter bestätigen.",
    suggestionsLabel: "Adressvorschläge",
  },
  askingVsRealized: {
    eyebrow: "Angebots- vs. realisierte Preise",
    title: "Der Angebotspreis ist erst der Anfang.",
    askingLabel: "Was im Inserat steht",
    askingValue: "250.000 €",
    askingNote: "Die Erwartung des Verkäufers",
    realizedLabel: "Wofür die Nachbarwohnung verkauft wurde",
    realizedValue: "218.000 €",
    realizedNote: "Was tatsächlich gezahlt wurde (ETN-Register)",
    question: "Der Angebotspreis ist eine Erwartung. Der Verkaufspreis ist eine erfasste Zahlung.",
    answer:
      "Für die Beurteilung eines Preises brauchen Sie vergleichbare Verkäufe, deren Zeitpunkt und die Unterschiede zwischen Immobilien. Der Bericht führt sie zusammen und erklärt ihre Aussagekraft und Grenzen.",
    caveat:
      "Die Differenz zwischen Angebots- und realisiertem Preis ist nicht komplett Verhandlungsspielraum — ein Teil stammt aus Unterschieden zwischen den verglichenen Immobilien und aus dem breiteren geografischen Zuschnitt der Inserate. Mehr im Ratgeber.",
    cta: "Lesen, wie groß die Differenz ist und was sie bedeutet",
  },
  features: {
    eyebrow: "Funktionen",
    title: "Was können wir Ihnen bieten?",
    description:
      "Alles, was Sie für eine sicherere Immobilienkaufentscheidung brauchen — an einem Ort.",
    items: [
      {
        title: "Offizielle GURS-Daten",
        description:
          "Dieselben Daten, die der Verkäufer hat — jetzt auf Ihrer Seite.",
      },
      {
        title: "Kern-Marktanalyse",
        description:
          "Sehen Sie, wofür Immobilien in Ihrer Straße tatsächlich verkauft wurden.",
      },
      {
        title: "Standort-Einblicke",
        description:
          "Finden Sie heraus, was den Wert beeinflusst — bevor es der Käufer nach Ihnen tut.",
      },
      {
        title: "KI-gestützte Bewertung",
        description:
          "Der Computer verknüpft 78+ Datenpunkte, für deren Durchsicht Sie selbst Wochen bräuchten.",
      },
      {
        title: "PDF-Verhandlungsblatt",
        description:
          "Ein übersichtliches PDF mit begründeten Argumenten für einen niedrigeren Preis und den konkreten Schritten, die Sie vor der Unterzeichnung nicht überspringen dürfen.",
      },
      {
        title: "Verständlicher Bericht",
        description:
          "Ein übersichtlicher, gut strukturierter PDF-Bericht, bereit zum Speichern, Teilen oder Besprechen mit einem Experten.",
      },
    ],
  },
  howItWorks: {
    eyebrow: "Ablauf",
    title: "Wie funktioniert es?",
    description:
      "Adresse eingeben, verfügbare Informationen prüfen und die nächsten Schritte festlegen.",
    steps: [
      {
        title: "Angaben eingeben",
        description: "Geben Sie die Adresse der Immobilie für die Analyse ein.",
      },
      {
        title: "KI analysiert mehrere Quellen",
        description:
          "Marktpreise, Standort, vergleichbare Verkäufe, Risiken und Trends.",
      },
      {
        title: "Bericht erhalten",
        description:
          "Wert, Warnungen und Empfehlungen — leicht verständlich ohne Fachwissen.",
      },
      {
        title: "Mit Fakten verhandeln",
        description:
          "Konkrete Argumente für einen niedrigeren Preis — gestützt auf offizielle Daten.",
      },
    ],
  },
  testimonials: {
    eyebrow: "Vertrauen von vielen",
    title: "Was unsere Nutzer sagen",
    description:
      "Echte Erfahrungen von Maklern, Investoren und Immobilieneigentümern in ganz Slowenien.",
    items: [
      {
        name: "Marko",
        location: "Ljubljana",
        content:
          "Sehr zufrieden mit nReport. Ihre KI-Analyse entsprach eng den tatsächlichen Verkaufspreisen. Großartiger Service!",
      },
      {
        name: "Ana",
        location: "Ljubljana",
        content:
          "Als lizenzierte Maklerin nutze ich nReport jetzt für alle meine Kunden. Die Berichte sind unverzichtbar — detailliert, zuverlässig und professionell erstellt. Meine Käufer sind immer beeindruckt.",
      },
      {
        name: "Matej",
        location: "Maribor",
        content:
          "Bei der Bewertung von Gewerbeflächen für die Expansion hat nReport Monate an Arbeit gespart. Wir haben drei Bewertungen in einer Woche erhalten, alle basierend auf offiziellen GURS-Daten. Die Investition hat sich am ersten Tag ausgezahlt.",
      },
      {
        name: "Petra",
        location: "Celje",
        content:
          "Bei der Vermögensaufteilung brauche ich zuverlässige Bewertungen. nReport liefert genau das — professionelle Berichte auf Basis offizieller Daten.",
      },
      {
        name: "Gregor",
        location: "Koper",
        content:
          "Ich empfehle nReport meinen Kunden für eine schnelle informative Bewertung. Innerhalb von 24 Stunden erhalten sie einen professionellen Bericht, der ihnen hilft, den Immobilienwert zu verstehen. Ausgezeichneter Service!",
      },
    ],
    prevAria: "Vorheriges Testimonial",
    nextAria: "Nächstes Testimonial",
    goToAria: "Zum Testimonial gehen",
  },
  pricing: {
    badge: "Paket wählen",
    title: "Berichtspakete",
    subtitle: "Wählen Sie das Paket für Ihre Suche. Jedes kostenpflichtige Paket enthält den vollständigen Bericht.",
    subtitleHighlight:
      "1 Credit = 1 Bericht. Credits sind 1 Jahr ab Kauf gültig.",
    popular: "Zum Vergleich von Optionen",
    freePlan: {
      badge: "Kostenlos",
      name: "Kostenlose Berichtsvorschau",
      subtitle: "1 Zusammenfassung",
      priceLabel: "€0",
      specialNote: "Nach E-Mail-Verifizierung",
      features: [
        "Offizielle GURS-Daten und amtlicher Wert",
        "Anzahl der Verkäufe und Inserate in der Umgebung",
        "Offene Risiken, die Sie vor der Unterschrift prüfen",
      ],
      lockedNote:
        "Unsere Wertschätzung und der Rest des Berichts (Marktanalyse, Lage, Grundbuch, ETN-Verkäufe) werden mit 1 Credit freigeschaltet",
      buttonText: "Kostenlos testen",
      buttonTextLoggedIn: "Zum Dashboard",
    },
    addressWarning:
      'Die Adresse „{{address}}" enthält keine Hausnummer. Bitte gehen Sie zurück und geben Sie eine vollständige Adresse mit Hausnummer ein, um Credits zu kaufen.',
    anchorAppraisalLabel: "Gutachten eines gerichtlichen Sachverständigen",
    anchorAppraisalValue: "400 – 600 €",
    anchorOursLabel: "nReport Informationsbericht",
    anchorOursValue: "ab 23,40 €",
    anchorNote:
      "Der Bericht ersetzt kein amtliches Gutachten — er ist eine schnellere und günstigere Grundlage für die Entscheidung, ob der Preis realistisch ist und ob sich ein Gutachten überhaupt lohnt.",
    perReport: "/ Bericht",
    includesGurs: "GURS-Auszug und Grundbuchprüfung (ZK) in allen Paketen enthalten",
    notOfficialAppraisal: "Ein informativer Bericht für bessere Entscheidungen — er ersetzt keine amtliche Bewertung",
    selectAddressFirst: "Wählen Sie zuerst eine Adresse aus",
    aptPrefix: "Whg.",
    securePayment: "100 % sichere Zahlung",
    securePaymentDesc: "über Stripe • Keine versteckten Gebühren",
    allCards: "💳 Alle Karten",
    instantActivation: "⚡ Sofortige Aktivierung",
    toastAddressRequired: "Adresse erforderlich",
    toastAddressRequiredDesc:
      "Bitte wählen Sie eine Immobilienadresse aus, bevor Sie Credits kaufen.",
    toastInvalidAddress: "Ungültige Adresse",
    toastInvalidAddressDesc:
      "Die Adresse muss eine Hausnummer enthalten. Bitte geben Sie eine vollständige Adresse ein.",
    toastAptRequired: "Wohnungsnummer erforderlich",
    toastAptRequiredDesc:
      "Bitte geben Sie die Wohnungsnummer ein, bevor Sie Credits kaufen.",
    plans: [
      {
        name: "Paket 1",
        subtitle: "1 Bericht erhalten",
        features: [
          "Offizielle GURS-Daten",
          "Marktpreisvergleich (abgeschlossene Verkäufe + Inserate)",
          "Grundbuchstatusprüfung",
          "KI-Wertschätzung",
          "PDF-Verhandlungsblatt mit Argumenten für einen niedrigeren Preis",
        ],
        buttonText: "1 Credit kaufen",
      },
      {
        name: "Paket 2",
        subtitle: "2 Berichte",
        specialNote: "Ideal zum Vergleich mehrerer Immobilien",
        features: [
          "PDF-Verhandlungsblatt mit Argumenten für einen niedrigeren Preis",
          "Alles aus Paket 1",
          "20 % pro Bericht sparen",
        ],
        buttonText: "2 Credits kaufen",
      },
      {
        name: "Paket 3",
        subtitle: "5 Berichte",
        specialNote: "Niedrigster Preis pro Bericht",
        features: [
          "PDF-Verhandlungsblatt mit Argumenten für einen niedrigeren Preis",
          "Alles aus Paket 1",
          "40 % pro Bericht sparen",
        ],
        buttonText: "5 Credits kaufen",
      },
    ],
    buy: "Kaufen",
  },
  faq: {
    eyebrow: "Hilfe",
    title: "Häufig gestellte Fragen",
    description:
      "Antworten auf die häufigsten Fragen zu unseren Immobilienberichten.",
    contactTitle: "Haben Sie eine Frage?",
    contactDesc: "Schreiben Sie uns — wir helfen Ihnen gerne weiter.",
    contactCta: "Frage senden",
    items: FAQ_DE,
  },
  ratingStats: {
    eyebrow: "Zufriedenheit",
    title: "Zufriedenheit unserer Nutzer",
    description:
      "Echtzeit-Bewertungen und Empfehlungen von Nutzern, die bereits einen Bericht erhalten haben.",
    recommend: "der Nutzer empfehlen uns",
    averageRating: "Durchschnittliche Bewertung",
    votes: "Stimmen",
  },
  footer: {
    tagline:
      "Unabhängige Immobilienberichte für Slowenien. Verständliche Fakten, Grundlagen und nächste Schritte.",
    legalHeading: "Rechtliche Informationen",
    terms: "Nutzungsbedingungen",
    privacy: "Datenschutzerklärung",
    cookies: "Cookies",
    dataDeletion: "Datenlöschung",
    cookieSettings: "Cookie-Einstellungen",
    trustedSources: "Vertrauenswürdige Quellen",
    trustedSourcesDesc:
      "Öffentliche Register und Marktdaten in einem Bericht. Die Verfügbarkeit einzelner Daten und Auszüge hängt von der Immobilie ab.",
    copyright: "© 2026 Noventis d.o.o. Alle Rechte vorbehalten.",
    slogan: "nReport.ai — smarter Immobilienkauf",
    backToTop: "Nach oben",
  },
  newsletter: {
    heading: "nReport Newsletter",
    description:
      "Immobilienmarkt-Analysen, neue Ratgeber und Produkt-Updates. Einmal im Monat, kein Spam.",
    placeholder: "sie@email.de",
    submit: "Abonnieren",
    success: "Danke! Sie haben den Newsletter abonniert.",
    error: "Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.",
    invalidEmail: "Bitte geben Sie eine gültige E-Mail-Adresse ein.",
    privacy: "Jederzeit abbestellbar.",
    optIn: "Ich möchte Neuigkeiten zum Immobilienmarkt erhalten (jederzeit abbestellbar).",
  },
  sampleExplorer: {
    loadError: "Die Vorschau konnte nicht geladen werden. Sie können das vollständige Muster weiterhin herunterladen.",
    loading: "Muster wird geöffnet…",
    "title": "Einen Musterbericht erkunden",
    "description": "Ein Überblick über Schätzung, Belege und offene Fragen. Das Beispiel ist fiktiv; das vollständige Muster-PDF ist auf Slowenisch.",
    "close": "Muster schließen",
    "sections": "Musterabschnitte",
    "value": "Wert",
    "evidence": "Belege",
    "checks": "Prüfungen",
    "valueTitle": "Ein Preis braucht Kontext.",
    "valueNote": "Die Schätzung ist ein Ausgangspunkt zur Beurteilung eines Angebotspreises. Die Spanne zeigt Unsicherheit; eine einzelne Zahl ersetzt weder eine Besichtigung noch ein Gutachten.",
    "salesNote": "Erfasste Verkäufe zeigen tatsächlich bezahlte Preise, Termine und vergleichbare Immobilien.",
    "registryNote": "Fläche, Baujahr und Immobilienkennungen helfen festzustellen, was Sie vergleichen.",
    "listingsNote": "Aktuelle Inserate zeigen die Erwartungen der Verkäufer. Angebotspreise sind keine erzielten Verkaufspreise.",
    "askingPrices": "Angebotspreise",
    "availability": "Verfügbare Quellen und Vergleichsdaten hängen von der Immobilie ab.",
    "checkTitle": "Wissen, was noch zu prüfen ist.",
    "checkNote": "Klar gekennzeichnete offene Fragen helfen, die nächsten Schritte vor der Unterschrift zu planen. Ein verfügbarer Grundbuchauszug ist die Grundlage zur Prüfung von Eigentum und Belastungen.",
    "openPdf": "Vollständiges PDF öffnen",
    "back": "Zurück zur Übersicht",
    "price": "1 vollständiger Bericht · {{price}}",
    "cta": "Bericht auswählen"
  },
  pdfPreview: {
    title: "nReport Musterbericht",
    download: "PDF herunterladen",
    downloadShort: "Herunterladen",
    orderReport: "Bericht bestellen",
    iframeTitle: "Muster-PDF-Bericht",
  },
  contact: {
    eyebrow: "Kontakt",
    title: "Senden Sie uns eine Frage",
    description:
      "Füllen Sie das Formular aus — wir melden uns so schnell wie möglich bei Ihnen.",
    nameLabel: "Vollständiger Name",
    namePlaceholder: "Max Mustermann",
    emailLabel: "E-Mail-Adresse",
    emailPlaceholder: "max@example.com",
    messageLabel: "Ihre Frage",
    messagePlaceholder: "Beschreiben Sie Ihre Frage...",
    successTitle: "Nachricht gesendet",
    successDesc:
      "Vielen Dank für Ihre Frage. Wir antworten so schnell wie möglich.",
    errorSend: "Die Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut.",
    validation: {
      nameRequired: "Name ist erforderlich",
      nameTooLong: "Name ist zu lang",
      emailInvalid: "Ungültige E-Mail-Adresse",
      emailTooLong: "E-Mail ist zu lang",
      messageRequired: "Nachricht ist erforderlich",
      messageTooLong: "Nachricht ist zu lang",
    },
  },
  auth: {
    signedOutTitle: "Abgemeldet",
    signedOutDesc: "Sie wurden erfolgreich abgemeldet.",
    signOutError: "Abmelden fehlgeschlagen. Bitte versuchen Sie es erneut.",
    login: "Anmelden",
    register: "Registrieren",
    loginDesc: "Melden Sie sich an, um auf Ihre Credits und Berichte zuzugreifen.",
    registerDesc:
      "Erstellen Sie ein Konto, um Credits zu kaufen und auf Berichte zuzugreifen. Nach der E-Mail-Verifizierung erhalten Sie einen kostenlosen Credit für einen Vorschaubericht.",
    displayName: "Anzeigename",
    displayNamePlaceholder: "z. B. Max Mustermann",
    email: "E-Mail",
    password: "Passwort",
    showPassword: "Passwort anzeigen",
    hidePassword: "Passwort verbergen",
    legalNotice: "Mit der Registrierung akzeptieren Sie unsere",
    legalTerms: "AGB",
    legalPrivacy: "Datenschutzerklärung",
    passwordHint:
      "Das Passwort muss mindestens 8 Zeichen lang sein und Groß- und Kleinbuchstaben sowie eine Zahl enthalten.",
    loginSubmit: "Anmelden",
    registerSubmit: "Registrieren",
    loginWithGoogle: "Mit Google anmelden",
    registerWithGoogle: "Mit Google registrieren",
    loginWithFacebook: "Mit Facebook anmelden",
    registerWithFacebook: "Mit Facebook registrieren",
    noAccount: "Noch kein Konto? Registrieren",
    hasAccount: "Bereits ein Konto? Anmelden",
    invalidEmail: "Geben Sie eine gültige E-Mail-Adresse ein",
    forgotPassword: "Passwort vergessen?",
    resetEnterEmail: "Geben Sie zuerst Ihre E-Mail-Adresse ein.",
    resetSentTitle: "Link zum Zurücksetzen gesendet",
    resetSentDesc: "Prüfen Sie Ihren Posteingang auf einen Link zum Zurücksetzen des Passworts.",
    resetError: "Der Link zum Zurücksetzen konnte nicht gesendet werden. Bitte erneut versuchen.",
    recoveryTitle: "Neues Passwort festlegen",
    recoveryDesc: "Wählen Sie ein neues Passwort für Ihr Konto.",
    recoverySubmit: "Passwort aktualisieren",
    passwordUpdatedTitle: "Passwort aktualisiert",
    passwordUpdatedDesc: "Sie können sich jetzt mit Ihrem neuen Passwort anmelden.",
    displayNameTooShort: "Der Anzeigename muss mindestens 2 Zeichen lang sein",
    passwordTooShort: "Das Passwort muss mindestens 8 Zeichen lang sein",
    passwordNeedsLower: "Das Passwort muss mindestens einen Kleinbuchstaben enthalten",
    passwordNeedsUpper: "Das Passwort muss mindestens einen Großbuchstaben enthalten",
    passwordNeedsDigit: "Das Passwort muss mindestens eine Zahl enthalten",
    invalidCredentials: "Ungültige Anmeldedaten",
    resendConfirmationTitle: "Bestätigungs-E-Mail gesendet",
    resendConfirmationSent: "Prüfen Sie Ihren Posteingang auf einen neuen Verifizierungslink.",
    resendConfirmationFailed: "Die Verifizierungs-E-Mail konnte nicht erneut gesendet werden. Bitte versuchen Sie es in einigen Minuten erneut.",
    userExists: "Ein Benutzer mit dieser E-Mail-Adresse existiert bereits",
    weakPassword:
      "Dieses Passwort ist zu häufig und leicht zu erraten. Bitte wählen Sie ein stärkeres Passwort.",
    registerSuccess: "Registrierung erfolgreich",
    registerSuccessDesc:
      "Prüfen Sie Ihren Posteingang, um Ihre E-Mail zu bestätigen. Nach der Verifizierung erhalten Sie einen kostenlosen Credit für einen Vorschaubericht.",
    suspiciousActivity:
      "Ungewöhnliche Aktivität erkannt. Bitte warten Sie einige Minuten.",
    googleError: "Google-Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.",
    facebookError: "Facebook-Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.",
    redirectingToGoogle: "Weiterleitung zu Google...",
    completingSignIn: "Anmeldung wird abgeschlossen...",
  },
  dashboard: {
    title: "Dashboard",
    loginPrompt: "Melden Sie sich an, um auf Ihr Dashboard zuzugreifen.",
    loadingSession: "Anmeldung wird abgeschlossen...",
    welcome: "Willkommen, {{name}}!",
    subtitle: "Ihre Berichte, Credits und Bestellungen — alles an einem Ort.",
    orderTitle: "Immobilienbericht bestellen",
    orderDesc: "Geben Sie die Adresse der Immobilie ein, um Ihren Bericht vorzubereiten.",
    orderCta: "Bericht bestellen (1 Credit)",
    orderPaidCta: "Detaillierten Bericht bestellen (1 Credit)",
    orderPreviewCta: "Kostenlose Vorschau erhalten",
    orderPaidDesc: "Geben Sie die Adresse der Immobilie ein. Ein detaillierter Bericht verbraucht 1 gekauften Credit.",
    orderPreviewDesc: "Sie haben einen kostenlosen Credit für eine Berichtsvorschau. Zum Freischalten des detaillierten Berichts benötigen Sie einen gekauften Credit.",
    insufficientCredits: "Nicht genügend Credits",
    insufficientCreditsDesc:
      "Sie haben nicht genügend gültige Credits, um einen Bericht zu bestellen. 1 Credit ist erforderlich.",
    insufficientCreditsShort:
      "Sie haben nicht genügend gültige Credits, um einen Bericht zu bestellen.",
    orderError: "Fehler bei der Berichtsbestellung",
    propertyNotFound: "Adresse nicht im Kataster",
    propertyNotFoundDesc:
      "Die Adresse wurde im GURS-Register nicht gefunden. Prüfen Sie die Hausnummer und ob die Adresse korrekt ist.",
    apartmentNotFound: "Wohnung nicht gefunden",
    apartmentNotFoundDesc:
      "Unter dieser Adresse wurde im Kataster keine Wohnung mit dieser Einheitennummer gefunden.",
    orderSuccess: "Bericht bestellt",
    orderSuccessDesc: "Ihr Bericht wird vorbereitet.",
    orderHeldDesc: "Wir ergänzen den Grundbuchauszug und senden Ihnen den Bericht innerhalb von 1 Werktag per E-Mail.",
    genericError: "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.",
    statCredits: "Verfügbare Credits",
    statInProgress: "In Bearbeitung",
    statCompleted: "Abgeschlossene Berichte",
    statInProgressInline: "{{count}} in Bearbeitung",
    statCompletedInline: "{{count}} abgeschlossen",
    lowCreditsTitle: "Ihre Credits sind aufgebraucht",
    lowCreditsDesc:
      "Kaufen Sie unten Credits, um Ihren nächsten Immobilienbericht zu bestellen.",
    creditHistoryToggle: "Credit-Verlauf",
    newReportEyebrow: "Neuer Bericht",
    reportsEyebrow: "Ihre Berichte",
    loadError: {
      retry: "Erneut versuchen",
      unavailable: "Nicht geladen",
      hint: "Ihre Daten sind nicht verloren. Prüfen Sie Ihre Verbindung und versuchen Sie es erneut.",
      credits: "Ihr Credit-Guthaben konnte nicht geladen werden.",
      orders: "Ihre Berichte konnten nicht geladen werden.",
      transactions: "Ihr Credit-Verlauf konnte nicht geladen werden.",
    },
    expiringCredits: {
      title: "{count} Credits verfallen bald",
      desc: "Ihre {count} nicht genutzten Credits verfallen am {date}. Danach können sie nicht mehr eingelöst werden — nutzen Sie sie also rechtzeitig.",
    },
    bulk: {
      addAddress: "Zur Liste hinzufügen",
      eyebrow: "Mehrere Immobilien",
      title: "Mehrere Berichte auf einmal bestellen",
      desc: "Fügen Sie Immobilien zur Liste hinzu und bestellen Sie sie in einem Schritt. Jeder Bericht kostet einen Credit.",
      apartment: "Wohnung Nr.",
      duplicate: "Diese Immobilie steht bereits auf der Liste.",
      remove: "Von der Liste entfernen",
      placed: "Bestellt",
      failed: "Fehlgeschlagen",
      skippedNoCredits: "Nicht bestellt — Credits aufgebraucht.",
      needCredits: "{count} Immobilien auf der Liste · Sie haben {balance} Credits",
      partialWarning: "Die ersten {n} werden bestellt.",
      submit: "{count} Berichte bestellen",
      running: "Bestelle {done}/{total} …",
      doneTitle: "Bestellung abgeschlossen",
      doneDesc: "{placed} von {total} Berichten bestellt.",
    },
  },
  checkout: {
    tagline: "Immobilienberichte",
    back: "Zurück",
    orderSummary: "Bestellübersicht",
    unlockTitle: "Bericht freischalten",
    unlockDesc: "Der Credit wird direkt nach der Zahlung für diesen Bericht eingelöst.",
    package: "Paket",
    credits: "Credits",
    address: "Adresse",
    total: "Gesamt",
    includesVat: "Inkl. MwSt.",
    vatExempt: "Endpreis. Keine Umsatzsteuer (Artikel 94 Abs. 1 des slowenischen UStG, ZDDV-1).",
    securePaymentTitle: "Sichere Zahlung",
    securePaymentDesc:
      "Ihre Zahlungsdaten werden von Stripe verarbeitet. Ihre Daten sind sicher und verschlüsselt.",
    creditsValidity: "1 Credit = 1 Bericht. Credits sind 1 Jahr ab Kauf gültig.",
    paymentTitle: "Zahlung",
    paymentDesc: "Schließen Sie Ihren Kauf mit sicherer Zahlung über Stripe ab",
    loadingPayment: "Zahlungssystem wird geladen...",
    legalTitle: "Rechtlicher Hinweis",
    legalDesc:
      "Der Bericht dient ausschließlich Informationszwecken und stellt keine offizielle Immobilienbewertung dar. Daten und Analysen basieren auf öffentlich zugänglichen Unterlagen und statistischen Modellen, die eine professionelle Bewertung durch einen lizenzierten Gutachter nicht ersetzen.",
    missingData: "Erforderliche Kaufdaten fehlen. Bitte versuchen Sie es erneut.",
    paymentEyebrow: "Zahlung",
    paymentSuccess: "Zahlung erfolgreich!",
    redirecting: "Weiterleitung...",
    creditOne: "Credit",
    creditTwo: "Credits",
    creditFew: "Credits",
    creditMany: "Credits",
  },
  paymentSuccess: {
    processingEyebrow: "Zahlung",
    processingTitle: "Ihre Zahlung wird bestätigt …",
    processingDesc: "Das dauert nur wenige Sekunden. Bitte lassen Sie diese Seite geöffnet.",
    loginTitle: "Anmelden, um Zahlungsstatus anzuzeigen",
    loginDesc: "Ihre Zahlung ist sicher. Melden Sie sich mit dem Konto an, mit dem Sie bezahlt haben – wir bestätigen Ihre Credits direkt hier.",
    successEyebrow: "Zahlung bestätigt",
    successTitle: "Vielen Dank, Zahlung erhalten",
    successDesc: "Ihre Credits sind auf Ihrem Konto und sofort einsatzbereit.",
    paymentFailed: "Zahlung war nicht erfolgreich",
    processingError: "Bei der Verarbeitung Ihrer Bestellung ist ein Fehler aufgetreten.",
    stepsLabel: "Bestellfortschritt",
    stepPayment: "Zahlung erhalten",
    stepPaymentActive: "Zahlung wird bestätigt …",
    stepCredits: "Credits gutgeschrieben",
    stepCreditsActive: "Credits werden Ihrem Konto gutgeschrieben …",
    stepCreditsDone: "Ihrem Konto gutgeschrieben: {{credits}}",
    stepCreditsWaiting: "Dauert länger als üblich – die Credits werden automatisch gutgeschrieben.",
    stepOrder: "Bericht gestartet",
    stepOrderActive: "Bericht für die eingegebene Adresse wird gestartet …",
    stepOrderDone: "Wir haben den Bericht für die eingegebene Adresse gestartet. Er erscheint in Kürze in Ihrem Dashboard.",
    stepOrderFailed: "Der Bericht konnte nicht automatisch gestartet werden. Ihr Credit bleibt erhalten – bestellen Sie den Bericht im Dashboard.",
    stepUnlock: "Vollständiger Bericht",
    stepUnlockActive: "Ihr Bericht wird freigeschaltet …",
    stepUnlockDone: "Der vollständige Bericht ist bereit.",
    stepUnlockHeld: "Bezahlt. Den vollständigen Bericht mit Grundbuchauszug liefern wir innerhalb von 1 Werktag.",
    stepUnlockFailed: "Ihr Credit bleibt erhalten – schalten Sie den Bericht mit einem Klick auf seiner Seite frei.",
    stepNext: "Bericht bestellen",
    stepNextDesc: "Geben Sie im Dashboard die Adresse einer Immobilie ein. Jeder Bericht verbraucht 1 Credit.",
    statusDone: "erledigt",
    statusActive: "läuft",
    statusPending: "ausstehend",
    statusNext: "nächster Schritt",
    statusAttention: "erfordert Aufmerksamkeit",
    delayedTitle: "Zahlung erhalten, Credits folgen",
    delayedDesc: "Ihre Zahlung ist bestätigt, die Gutschrift dauert aber länger als üblich – meist ist das innerhalb einer Minute erledigt. Prüfen Sie erneut oder öffnen Sie Ihr Dashboard, dort erscheinen die Credits.",
    checkAgain: "Erneut prüfen",
    checkingAgain: "Wird geprüft …",
    errorTitle: "Wir konnten Ihre Zahlung noch nicht bestätigen",
    errorDesc: "Falls Ihnen der Betrag abgebucht wurde, sind Ihre Credits sicher. Versuchen Sie es gleich noch einmal oder schreiben Sie uns.",
    missingSessionTitle: "Diese Zahlung wurde nicht gefunden",
    missingSessionDesc: "Dem Link fehlt die Zahlungsreferenz. Falls Ihnen der Betrag abgebucht wurde, erscheinen die Credits in Ihrem Dashboard.",
    redirectDashboardIn: "Weiter zum Dashboard in {{seconds}} s.",
    redirectReportIn: "Ihr Bericht öffnet sich in {{seconds}} s.",
    stayHere: "Auf dieser Seite bleiben",
    goToDashboard: "Zum Dashboard",
    openReport: "Bericht öffnen",
    backHome: "Zurück zur Startseite",
    contactLead: "Fragen? Schreiben Sie uns an",
    announceConfirming: "Ihre Zahlung wird bestätigt.",
    announceCompleted: "Zahlung bestätigt. Ihrem Konto gutgeschrieben: {{credits}}.",
    guestTitle: "Zahlung erhalten!",
    guestDesc: "Wir haben Ihnen einen Login-Link per E-Mail gesendet, über den Sie auf Ihre Bericht-Credits zugreifen.",
    guestSpam: "Keine E-Mail innerhalb weniger Minuten? Prüfen Sie Ihren Spam-Ordner.",
  },
  postPurchaseSurvey: {
    question: "Kurze Frage — was hätte Sie fast vom Kauf abgehalten?",
    options: {
      price: "Der Preis",
      trust: "Zweifel an der Datengenauigkeit",
      unclear: "Unklar, was enthalten ist",
      technical: "Zahlungs-/technische Probleme",
      nothing: "Nichts — es war einfach",
    },
    skip: "Überspringen",
    thanks: "Danke für Ihr Feedback!",
  },
  paymentTrust: {
    acceptedMethods: "Wir akzeptieren alle gängigen Karten",
    securedByStripe: "Sichere Zahlung über Stripe",
    encryptedNote: "Ihre Kartendaten sind verschlüsselt und erreichen niemals unsere Server.",
  },
  checkoutSupport: {
    contact: "Support kontaktieren",
    cancellation: "14-tägiges Widerrufsrecht",
  },
  checkoutSteps: {
    package: "Paket",
    payment: "Zahlung",
    done: "Bestätigung",
  },
  checkoutUpsell: {
    title: "Brauchen Sie mehr als einen?",
    desc: "{{credits}} Berichte für €{{price}} — nur €{{per}} pro Bericht, {{discount}}% günstiger.",
    cta: "Dieses Angebot wählen",
  },
  paymentCanceled: {
    eyebrow: "Abgebrochen",
    title: "Zahlung abgebrochen",
    desc: "Ihre Zahlung wurde abgebrochen. Ihrem Konto wurden keine Beträge belastet.",
    help: "Wenn ein Fehler aufgetreten ist oder Sie Probleme bei der Zahlung haben, kontaktieren Sie uns für Hilfe.",
    backHome: "Zurück zur Startseite",
    tryAgain: "Erneut versuchen",
    contact: "Bei Zahlungsproblemen kontaktieren Sie uns unter info@nreport.ai",
  },
  notFound: {
    eyebrow: "Fehler 404",
    title: "Seite nicht gefunden",
    desc: "Die gesuchte Seite existiert leider nicht oder wurde verschoben.",
    backHome: "Zurück zur Startseite",
    goBack: "Zurück",
  },
  errorBoundary: {
    eyebrow: "Fehler",
    title: "Etwas ist schiefgelaufen",
    desc: "Es ist ein unerwarteter Fehler aufgetreten. Laden Sie die Seite neu oder wenden Sie sich an den Support.",
    retry: "Erneut versuchen",
    reload: "Seite neu laden",
    devDetails: "Entwicklerdetails",
  },
  quickPurchase: {
    title: "Schneller Credit-Kauf",
    popular: "Beliebt",
    packages: [
      { name: "1 Credit kaufen", description: "Einzelbericht" },
      { name: "2 Credits kaufen", description: "20 % sparen" },
      { name: "5 Credits kaufen", description: "40 % sparen" },
    ],
  },
  orderStatus: {
    title: "Meine Berichte",
    desc: "Abgeschlossene Berichte herunterladen oder laufende verfolgen",
    noOrders: "Noch keine Berichte",
    noOrdersHint: "Bestellen Sie unten Ihren ersten Immobilienbericht.",
    defaultTitle: "Immobilienbericht",
    apartment: "Wohnung",
    house: "Haus",
    property: "Immobilie",
    aptNo: "Nr.",
    downloadPdf: "PDF herunterladen",
    openPdf: "Bericht in neuem Tab öffnen",
    refresh: "Bestellungen aktualisieren",
    loadMore: "Ältere Berichte anzeigen",
    loadingMore: "Ältere Berichte werden geladen …",
    loadedCount: "{{count}} Berichte angezeigt",
    loadMoreError: "Ältere Berichte konnten nicht geladen werden. Bitte versuchen Sie es erneut.",
    dateUnknown: "Datum unbekannt",
    thumbnailAlt: "Kartenvorschau für {{address}}",
    readyBadge: "Fertig",
    previewBadge: "Vorschau",
    unlockHeldBadge: "Bezahlt – in Vorbereitung",
    hintCompleted: "Ihr Bericht steht zum Download bereit.",
    hintProcessing: "Wir analysieren die Immobilie — dauert in der Regel wenige Minuten.",
    hintPending: "Ihr Bericht ist in der Warteschlange und startet in Kürze.",
    hintFailed: "Etwas ist schiefgelaufen. Kontaktieren Sie den Support, wenn das anhält.",
    hintRefunded: "Der Bericht konnte nicht gestartet werden, daher wurde Ihr Guthaben zurückerstattet. Sie können ihn erneut bestellen.",
    hintNeedsReview: "Unsere automatische Qualitätsprüfung hat eine Unstimmigkeit gefunden, daher wird der Bericht vor der Zustellung manuell geprüft. Wir benachrichtigen Sie, sobald er fertig ist.",
    hintAwaitingApproval: "Ihr Bericht ist fertig und wird vor dem Versand noch persönlich geprüft. Sie erhalten ihn per E-Mail, sobald er freigegeben ist.",
    statusUnknown: "Unbekannt",
    statusCompleted: "Abgeschlossen",
    statusProcessing: "In Bearbeitung",
    statusPending: "Ausstehend",
    statusFailed: "Fehlgeschlagen",
    statusCancelled: "Abgebrochen",
    statusNeedsReview: "In Prüfung",
    viewReport: "Bericht ansehen",
    generation: {
      live: "Live",
      phaseQueued: "In der Warteschlange",
      phaseChecking: "Immobilie wird geprüft",
      phaseWriting: "Analyse wird geschrieben",
      phaseMarket: "Marktanalyse und Prüfung",
      phasePdf: "PDF wird erstellt",
      phaseDone: "Fertig",
      etaMinutes: "noch etwa {{count}} Min.",
      etaUnderMinute: "noch weniger als eine Minute",
      etaLonger: "Dauert länger als üblich – der Bericht wird trotzdem geliefert.",
      typicalDuration: "Dauert normalerweise etwa 3 Minuten.",
      heldTitle: "Wir beschaffen den Grundbuchauszug",
      heldDesc: "Ein bezahlter Bericht enthält den Grundbuchauszug. Den fertigen Bericht senden wir Ihnen innerhalb von 1 Werktag per E-Mail.",
      activeTitle: "Berichte in Arbeit",
      activeDesc: "Verfolgen Sie den Fortschritt live. Sobald der Bericht fertig ist, erscheint hier der Link.",
      viewAll: "Alle Berichte",
      duplicateTitle: "Für diese Adresse gibt es bereits einen Bericht",
      duplicateDesc: "Eine Bestellung für {{address}} existiert bereits ({{status}}). Eine neue Bestellung verbraucht ein weiteres Credit und erstellt einen zweiten Bericht für dieselbe Immobilie.",
      duplicateConfirm: "Trotzdem bestellen (1 Credit)",
      duplicateCancel: "Abbrechen",
    },
  },
  reportDetail: {
    navigationTitle: "Bericht erkunden",
    loading: "Bericht wird geladen...",
    notFound: "Bericht nicht gefunden.",
    dataUnavailable: "Berichtsdaten sind noch nicht verfügbar.",
    backToDashboard: "Zurück zum Dashboard",
    paywall: {
      eyebrow: "Kostenlose Vorschau",
      title: "Nur noch ein Schritt bis zum vollständigen Bericht",
      description: "Für {{address}} haben wir die Daten bereits gesammelt und ausgewertet. Das schalten Sie frei:",
      findingsTitle: "Was wir zu dieser Immobilie gefunden haben",
      estimateLabel: "Geschätzter Marktwert",
      estimateNote: "mit Spanne und Verlässlichkeitsstufe",
      ceilingLabel: "Gerundeter Orientierungswert",
      ceilingNote: "gerundeter Ausgangspunkt der Analyse, anhand Ihrer Umstände zu prüfen",
      salesLabel: "Realisierte Verkäufe in der Umgebung",
      salesYears: "{{from}}–{{to}}",
      salesClosest: "nächster {{distance}} m entfernt",
      listingsLabel: "Vergleichbare Inserate",
      listingsNote: "durchschnittlicher Angebotspreis pro m²",
      gapLabel: "Angebots- vs. erzielte Preise",
      gapNote: "Abstand zwischen Inseratspreis und Verkaufspreis in der Umgebung",
      argumentsLabel: "Verhandlungsargumente",
      argumentsNote: "mit druckbarem Blatt für die Besichtigung",
      riskLabel: "Vor der Unterschrift prüfen",
      riskNote: "Was genau zu tun ist, steht im vollständigen Bericht.",
      includesTitle: "Der vollständige Bericht enthält",
      includes: {
        estimate: "Informative Wertschätzung mit Spanne und Verlässlichkeitsstufe",
        method: "Schritt-für-Schritt-Berechnung aus amtlichen Daten",
        sales: "Realisierte Verkäufe (ETN) auf der Karte mit Preisen und Daten",
        trend: "Preisentwicklung in der Umgebung nach Jahren",
        listings: "Vergleichbare Inserate mit Preisen und Links",
        location: "Standortanalyse und Angebote in der Nähe",
        cadaster: "Wesentliche Gebäudemerkmale und Katasterdaten",
        risks: "Risikoliste und Schritte vor der Vertragsunterzeichnung",
        negotiation: "Verhandlungspunkte und druckbares Besichtigungsblatt",
        attachments: "Anhänge: GURS-Bewertung und Katasterauszug",
        attachmentsEnergy: "Anhänge: GURS-Bewertung, Katasterauszug und Energieausweis",
        pdf: "Vollständiges PDF zum Herunterladen und Drucken",
      },
      priceNote: "Einmalzahlung · kein Abo",
      buyCta: "Vollständigen Bericht freischalten",
      instant: "Den vollständigen Bericht mit Grundbuchauszug liefern wir innerhalb von 1 Werktag.",
      heldTitle: "Bezahlt – wir bereiten Ihren vollständigen Bericht vor",
      secure: "Sichere Kartenzahlung über Stripe",
      sample: "Beispiel eines vollständigen Berichts ansehen (PDF)",
      bundle: "Mehrere Immobilien im Blick? 5 Berichte für {{price}} ({{per}} pro Bericht)",
      stickyLabel: "Kostenlose Vorschau",
      stickyCta: "Für {{price}} freischalten",
      unlockCta: "Bericht freischalten (1 Credit)",
      unlocking: "Wird freigeschaltet...",
      creditsAvailable: "Sie haben {{count}} gekaufte Credits verfügbar",
      unlockedTitle: "Bericht freigeschaltet",
      unlockedDesc: "Der vollständige Bericht ist jetzt verfügbar.",
      unlockedPdfPending: "Bericht freigeschaltet. Das vollständige PDF wird noch erstellt — versuchen Sie den Download in einem Moment erneut.",
      unlockFailedTitle: "Freischaltung fehlgeschlagen",
      downloadPreview: "Vorschau herunterladen",
    },
    attachments: {
      title: "Anhänge",
      description:
        "Quelldokumente zu diesem Bericht (auch im heruntergeladenen PDF enthalten).",
      valuation: "Immobilienbewertung (GURS)",
      cadastral: "Katasterauszug (GURS)",
      energy: "Energieausweis",
      uploaded: "Hochgeladenes Dokument",
    },
    badge: "nReport.ai Immobilienbericht",
    title: "Immobilienanalyse",
    sectionOverviewEyebrow: "Immobilienübersicht",
    sectionOverviewTitle: "Wesentliche Merkmale",
    sectionOverviewDesc:
      "Art und Nutzung der Immobilie, Lage im Gebäude, Raumaufteilung, Bauweise und Ausstattung.",
    sectionFeaturesEyebrow: "Zusammenfassung",
    sectionFeaturesTitle: "Wichtige Datenpunkte",
    sectionMarketEyebrow: "KI-Marktanalyse",
    sectionMarketTitle: "Erweiterte Marktanalyse",
    sectionMarketDesc:
      "Umfassende Marktanalyse mit KI und statistischen Modellen.",
    sectionMethodologyEyebrow: "Methodik",
    sectionMethodologyTitle: "Informativer Wert",
    sectionMethodologyDesc:
      "Eine gewichtete Schätzung, die den offiziellen GURS-Wert mit Inserats- und Transaktionspreisen abgleicht.",
    sectionLocationEyebrow: "Standort",
    sectionLocationTitle: "Standort im Kontext",
    sectionLocationDesc: "Analyse des Immobilienstandorts und der Umgebung.",
    sectionCadasterEyebrow: "Grundbuch",
    sectionCadasterTitle: "Analyse des Grundbuchauszugs",
    sectionValuationEyebrow: "Immobilienbewertung",
    sectionValuationTitle: "Offizieller Immobilienwert",
    sectionValuationDesc: "GURS-generalisierte Marktwerte von Gebäude und Grundstück.",
    sectionEtnEyebrow: "Transaktionshistorie",
    sectionEtnTitle: "Abgeschlossene Geschäfte in der Nähe",
    sectionEtnDesc:
      "Überblick über abgeschlossene Verkäufe vergleichbarer Immobilien aus dem ETN-Register.",
    sectionAdsEyebrow: "Marktüberblick",
    sectionAdsTitle: "Vergleichbare Inserate",
    sectionAdsDesc: "Ausgewählte Inserate mit den vergleichbarsten Kennzahlen.",
    sectionAdsDescCohort:
      "Eine repräsentative Auswahl von {{shown}} der {{total}} Inserate in der Angebotspreisanalyse. Durchschnitt, Spanne und Verlässlichkeit beruhen auf allen {{total}}.",
    adsScopeTitle: "Wie die vergleichbaren Inserate ausgewählt wurden",
    recommendations: "Empfehlungen",
    priceAnalysis: "Preisanalyse",
    marketPosition: "Marktposition",
    propertyCharacteristics: "Immobilienmerkmale",
    investmentInsights: "Investitions-Einblicke",
    buildingValuation: "Gebäudebewertung",
    landValuation: "Grundstücksbewertung",
    satelliteMap: "Satellitenansicht",
    streetView: "Street View",
    disclaimerTitle: "Rechtlicher Hinweis",
    disclaimerText:
      "Dieser Bericht dient ausschließlich Informationszwecken und stellt keine offizielle Immobilienbewertung dar. Daten und Analysen basieren auf öffentlich zugänglichen Unterlagen und statistischen Modellen, die einen lizenzierten Gutachter nicht ersetzen.",
    area: "Fläche",
    netArea: "Netto-Grundfläche",
    usableArea: "Nutzfläche",
    landArea: "Grundstücksgröße",
    yearBuilt: "Baujahr",
    metricInformativeValue: "Informativer Wert",
    metricGursValue: "Verallgemeinerter GURS-Wert",
    metricMarketPriceM2: "Marktschätzung (Inserate)",
    metricEtnPriceM2: "Realisierter Wert (ETN)",
    metricEtnGrowth: "Jährliches Preiswachstum (CAGR)",
    metricEtnGrowthHint: "Historisches Wachstum der realisierten Preise pro m² in der Umgebung ({{period}}). Keine Mietrendite und keine Prognose.",
    metricEtnHistoricalHint:
      "Durchschnitt älterer Verkäufe — im jüngsten Zeitraum gab es keinen Vergleichsverkauf, daher fließt er nicht in die Schätzung ein.",
    metricEtnGrowthHintNoPeriod:
      "Historisches Wachstum der realisierten Preise pro m² in der Umgebung. Keine Mietrendite und keine Prognose.",
    gursGapLabel: "Abweichung zu GURS",
    distributionTitle: "Schätzung neben vertraglichen Verkaufspreisen",
    distributionLead:
      "Die Grafik zeigt Vertragspreise je m² und die informative Schätzung. Die Vertragspreise sind hier weder zeitlich noch an die Modellbasis der Schätzung angepasst; der Unterschied allein belegt keinen Rabatt oder überhöhten Preis.",
    distributionAlt: "Verteilung der realisierten Preise pro m²",
    negotiationSheetDownload: "Verhandlungsblatt",
    negotiationEyebrow: "Verhandlungsblatt",
    negotiationTitle: "Argumente, gestützt auf Daten",
    negotiationDesc:
      "Jedes Argument unten stammt aus Daten dieses Berichts und nennt seine Quelle. Sie sind dazu gedacht, laut ausgesprochen zu werden — nicht erklärt.",
    negotiationCeilingLabel: "Gerundeter Orientierungswert",
    negotiationBatna:
      "Legen Sie Ihr persönliches Höchstangebot anhand Ihres Budgets, der Kauf- und Sanierungskosten sowie offener Fragen fest. Es kann vom Orientierungswert der Analyse abweichen.",
    negotiationSourcePrefix: "Quelle:",
    negotiationHowToTitle: "So nutzen Sie diese Argumente",
    negotiationHowTo: [
      "Beginnen Sie nicht mit dem Preis. Fragen Sie: „Wie sind Sie auf diesen Preis gekommen?“ Lassen Sie erklären, dann zeigen Sie die Daten.",
      "Zeigen statt fordern: „Ich habe die realisierten Verkäufe in der Umgebung angesehen — vergleichbare Objekte gingen für X weg. Wie erklären Sie die Differenz?“",
      "Entschuldigen Sie Ihr Angebot nicht. Es ist datengestützt; das Dokument ist Ihr Argument, nicht Sie.",
      "Wenn Sie eine Zahl nennen, schweigen Sie danach. Stille ist ein Verhandlungswerkzeug.",
      "Hören Sie „das ist unter Marktwert“, fragen Sie: „Welche realisierten Verkäufe belegen das?“",
    ],
    negotiationLimitsTitle: "Grenzen.",
    negotiationLimits:
      "Diese Analyse beruht auf öffentlichen Registern (GURS, ETN, Inserateplattformen) und ersetzt keine amtliche Bewertung. Realisierte Verkäufe sind der beste verfügbare Näherungswert, nicht die absolute Wahrheit. Das Ergebnis hängt auch von der Motivation des Verkäufers, den Finanzierungsbedingungen und Faktoren ab, die diese Analyse nicht vorhersehen kann.",
    keyFactsEyebrow: "Basisdaten",
    keyFactsTitle: "Die Immobilie auf einen Blick",
    addressSpreadTitle: "Spanne der erfassten Preise an derselben Adresse",
    addressSpreadAnd: "und",
    addressSpreadExplain:
      "Verkäufe an derselben Adresse können verschiedene Gebäudeeinheiten, Verkaufsdaten und Vertragsbedingungen betreffen. Die Preisspanne allein belegt weder Vergleichbarkeit noch einen Datenfehler. Die vollständige Ursache ist aus dem Register nicht erkennbar; berücksichtigen Sie die Angaben und Grenzen jedes Verkaufs.",
    selectionNoteTitle: "So wurden die Vergleichsverkäufe ausgewählt.",
    selectionNote:
      "Im Umkreis von {{radius}} km haben wir {{considered}} realisierte Verkäufe geprüft und {{used}} davon verwendet. Berücksichtigt wurden Verkäufe mit einer Fläche zwischen {{min}} und {{max}} m².",
    selectionNoteNoSize:
      "Im Umkreis von {{radius}} km haben wir {{considered}} realisierte Verkäufe geprüft und {{used}} davon verwendet. Die Fläche dieser Immobilie ist im GURS nicht erfasst, daher wurde nicht nach Größe gefiltert und die Vergleichbarkeit nach Größe ist nicht gewährleistet.",
    selectionNoteLandRatio: "Bei Häusern haben wir zusätzlich ein ähnliches Verhältnis von Grundstück zu Gebäude verlangt.",
    selectionNoteOutliers:
      "{{count}} Verkäufe wurden als statistische Ausreißer ausgeschlossen — ihr Preis pro m² lag so weit außerhalb der lokalen Spanne, dass eine nicht marktübliche Übertragung am wahrscheinlichsten ist.",
    selectionNoteCohort:
      "Die Schätzung stützt sich auf {{used}} der {{considered}} geprüften Verkäufe im Umkreis von {{radius}} km — jene aus den {{months}} Monaten vor dem Bewertungsstichtag ({{date}}).",
    selectionNoteCohortNone:
      "Keiner der {{considered}} geprüften Verkäufe im Umkreis von {{radius}} km ist ein Vergleichsverkauf aus den {{months}} Monaten vor dem Bewertungsstichtag ({{date}}); realisierte Verkäufe fließen daher nicht in die Schätzung ein.",
    selectionNoteSizeBand:
      "Berücksichtigt wurden Verkäufe mit einer Fläche zwischen {{min}} und {{max}} m².",
    selectionNoteNoSizeShort:
      "Die Fläche dieser Immobilie ist in GURS nicht erfasst, daher wurden Verkäufe nicht nach Größe ausgewählt.",
    selectionNoteHistorical:
      "{{count}} ältere Vergleichsverkäufe werden nur als historischer Kontext gezeigt (als älter markiert); sie fließen nicht in die Schätzung ein.",
    selectionNoteExcluded:
      "Ausgeschlossen: {{count}}",
    selectionExclIneligible:
      "{{count}} nicht als Vergleich geeignet (z. B. Anteilsverkäufe, Übertragungen zwischen nahestehenden Personen)",
    selectionExclNotComparable:
      "{{count}} wegen Größe, Grundstück-Gebäude-Verhältnis oder Preis",
    selectionExclOutliers:
      "{{count}} statistische Ausreißer",
    selectionExclDate:
      "{{count}} ohne gültiges Datum oder doppelt",
    evidence: {
      eyebrow: "Belege für die Schätzung",
      title: "Die stärksten Vergleichsverkäufe und der Weg zur Schätzung",
      desc: "Welche realisierten Verkäufe dieser Immobilie am ähnlichsten sind, worin sie sich unterscheiden und wie aus den beobachteten Preisen die Schätzung entsteht.",
      headlinePriced: "Gezeigt werden die {{shown}} ähnlichsten von {{priced}} Verkäufen in der Schätzung; alle bestimmen die Schätzung, nicht nur die gezeigten.",
      headlineThin: "Nur {{priced}} realisierte(r) Verkauf/Verkäufe der letzten 24 Monate fließen in die Schätzung ein: die Beleglage ist dünn.",
      headlineHistorical: "Kein vergleichbarer realisierter Verkauf in den letzten 24 Monaten; die älteren Verkäufe unten sind nur historischer Kontext.",
      headlineNoSales: "Kein vergleichbarer realisierter Verkauf in der Umgebung.",
      headlineNoArea: "Wir haben keinen informativen Wert berechnet, weil die Fläche der Immobilie im GURS nicht erfasst ist.",
      headlineNoRecent: "Wir haben keinen informativen Wert berechnet: kein vergleichbarer realisierter Verkauf in den letzten 24 Monaten und keine vergleichbaren Inserate.",
      headlineWithheld: "Wir haben keinen informativen Wert veröffentlicht: zu wenige oder zu stark streuende Vergleichsverkäufe für eine verlässliche Schätzung.",
      headlineContextSuffix: "Die älteren Verkäufe unten sind nur historischer Kontext.",
      colSale: "Verkauf",
      colDate: "Datum",
      colDistance: "Entfernung",
      colArea: "Fläche",
      colPrice: "Preis",
      colPricePerM2: "Preis/m²",
      colSimilarity: "Ähnlichkeit",
      sourceId: "ETN-Geschäfts-ID {{id}}",
      typeApartment: "Wohnung",
      typeHouse: "Haus",
      addressUnknown: "Adresse nicht veröffentlicht",
      atValuationDate: "zum Bewertungsstichtag {{price}}",
      contextMarker: "älterer Verkauf — nur Kontext",
      yearOnly: "Jahr {{year}}",
      knownDifferences: "Bekannte Unterschiede",
      dealNotes: "Besonderheiten des Verkaufs",
      areaBasis: "Flächenbasis",
      areaBasisDwelling: "verkaufte Netto-Grundfläche der Wohneinheit",
      areaBasisAllParts: "verkaufte Fläche aller Teile des Geschäfts",
      diffSizeSimilar: "ähnliche Fläche ({{area}})",
      diffSizeLarger: "{{pct}} größer ({{area}})",
      diffSizeSmaller: "{{pct}} kleiner ({{area}})",
      diffSizeUnknownSubject: "{{area}}; Fläche dieser Immobilie unbekannt",
      diffYearSame: "gleich alt ({{year}})",
      diffYearNewer: "{{years}} Jahre neuer ({{year}})",
      diffYearOlder: "{{years}} Jahre älter ({{year}})",
      diffYearUnknown: "Baujahr unbekannt",
      diffYearUnknownSubject: "Baujahr {{year}}; Baujahr dieser Immobilie unbekannt",
      diffLandSimilar: "ähnliches Grundstück ({{area}})",
      diffLandMore: "{{pct}} mehr Grundstück ({{area}})",
      diffLandLess: "{{pct}} weniger Grundstück ({{area}})",
      diffLandUnknownSubject: "Grundstück {{area}}; Grundstück dieser Immobilie unbekannt",
      diffLandNone: "kein Grundstück im Geschäft erfasst",
      flags: {
        ancillary_included: "Preis enthält Garage, Stellplatz oder Abstellraum (nicht in den m²)",
        outbuildings_included: "Preis enthält Neben- oder Wirtschaftsgebäude",
        minor_extra_land: "Verkauf enthält ein kleines Zusatzgrundstück",
        shared_land: "Grundstück als Miteigentumsanteil verkauft",
        vat_included: "Preis inkl. MwSt.",
        vat_rate_mixed: "Preis inkl. MwSt. zu verschiedenen Sätzen",
        new_build: "Neubau",
        voluntary_auction: "Verkauf in freiwilliger Versteigerung",
        gurs_market_data_issue: "GURS: Marktgeschäft mit unvollständigen Daten",
        gurs_unverified: "von GURS noch nicht geprüft",
      },
      priceAdjustment: "Preisanpassung",
      adjSize: "Größe {{pct}}",
      adjAge: "Baujahr {{pct}}",
      adjTotal: "gesamt {{pct}}",
      adjNone: "keine Anpassung (Größe und Baujahr stimmen überein oder sind unbekannt)",
      setTitle: "Gezeigte Verkäufe und die gesamte Analyse.",
      setWeighting: "In den ETN-Durchschnitt gehen alle Verkäufe der letzten {{months}} Monate ({{priced}}) ein, nicht nur die gezeigten, gewichtet nach Fläche (Summe der Preise / Summe der m²).",
      setWeightsAsking: "Realisierte Verkäufe tragen {{etn}} % der Schätzung, Angebotspreise ({{listings}} Inserate) den Rest.",
      setWeightsOnly: "Realisierte Verkäufe tragen {{etn}} % der Schätzung.",
      setEtnZero: "Realisierte Verkäufe haben in dieser Schätzung kein Gewicht.",
      setNone: "Kein realisierter Verkauf ist in die Schätzung eingeflossen.",
      setAverages: "Die gezeigten Verkäufe liegen im Schnitt bei {{shown}}, alle Verkäufe der Schätzung ({{priced}}) bei {{all}}.",
      setExcluded: "{{excluded}} von {{considered}} geprüften Verkäufen wurden ausgeschlossen; die Gründe stehen bei der Auswahl der Verkäufe.",
      similarityMethod: "Die Ähnlichkeit (0–100) ergibt sich aus Entfernung, Unterschied der Netto-Grundfläche, Unterschied im Baujahr, Alter des Verkaufs und bei Häusern dem Unterschied der Grundstücksgröße. Ein fehlender Wert zählt als halbe Abweichung. Der Immobilientyp stimmt bei allen Verkäufen überein, da nur Verkäufe desselben Typs ausgewählt werden.",
      bridgeTitle: "Von beobachteten Preisen zur Schätzung",
      legs: {
        etn: "Realisierte Verkäufe (ETN)",
        market: "Angebotspreise",
        gurs: "GURS-Verkehrswert (Massenbewertung)",
      },
      weight: "Gewicht {{pct}} %",
      observedEtn: "Beobachtet: Summe der Preise / Summe der m² aller {{n}} Verkäufe der Schätzung",
      observedMarket: "Beobachtet: durchschnittlicher Angebotspreis von {{n}} Inseraten der Analyse",
      observedGurs: "Beobachtet: GURS-Wert / Bewertungsfläche",
      steps: {
        etn_time_index: "Zeitliche Anpassung auf den Bewertungsstichtag {{pct}} (gemessen: lokale jährliche Preisänderung aus denselben Verkäufen)",
        etn_uplift: "Aufschlag auf ETN-Preise {{pct}} (Modellannahme, nicht unabhängig geprüft)",
        asking_haircut_measured: "Abschlag auf Angebotspreise {{pct}} (gemessenes Verhältnis Angebot/realisierter Preis; vermutlich Obergrenze)",
        asking_haircut_assumed: "Abschlag auf Angebotspreise {{pct}} (Annahme, kleiner als die gemessene Differenz)",
        gurs_uplift: "Aufschlag auf den GURS-Wert {{pct}} (Modellannahme, nicht unabhängig geprüft)",
        etn_comparable_adjustments: "Anpassung der Vergleichsverkäufe an Unterschiede in Größe und Baujahr {{pct}} (an registrierten Verkäufen gemessen; die Anpassung jedes Verkaufs steht beim Verkauf)",
        subject_position: "Stockwerk und Aufzug {{pct}} (amtlicher GURS-Lagefaktor für diese Wohnung; die Stockwerke der Vergleichsverkäufe sind nicht erfasst)",
      },
      statusMeasured: "gemessen",
      statusAssumed: "Annahme",
      effectOnEstimate: "Wirkung auf die Schätzung: {{eur}}",
      contribution: "Beitrag zur Schätzung: {{price}} × {{share}}",
      downweight: "Das Gewicht realisierter Verkäufe wird mit {{value}} multipliziert, weil die angepassten Angebotspreise deutlich höher liegen (Modellannahme, nicht unabhängig geprüft).",
      weightedPrice: "Gewichteter Preis pro m²",
      roundedFrom: "gerundet von {{value}}",
      timesArea: "× {{label}} {{area}} = informativer Wert",
      noSubjectAdjustments: "Für Unterschiede zwischen den Vergleichsverkäufen und dieser Immobilie (Größe, Alter, Grundstück, Zustand, Energieklasse, Stockwerk) nimmt die Schätzung keine Anpassung vor: für keinen davon gibt es einen geprüften lokalen Koeffizienten. Die Unterschiede werden genannt, aber nicht bewertet.",
      subjectAdjustmentsSizeAge: "Unterschiede in Größe und Baujahr werden mit Koeffizienten bewertet, die an registrierten Verkäufen gemessen wurden (kleinere Wohnungen erzielen einen höheren Preis pro m²); die Anpassung jedes gezeigten Verkaufs steht beim Verkauf.",
      subjectAdjustmentsPosition: "Stockwerk und Aufzug berücksichtigen wir mit dem amtlichen GURS-Lagefaktor für diese Wohnung.",
      subjectAdjustmentsRest: "Grundstück, Zustand und Energieklasse bewerten wir nicht, da wir dafür keinen gemessenen Koeffizienten haben.",
      subjectAdjustmentsRestNoSizeAge: "Größe, Alter, Grundstück, Zustand und Energieklasse bewerten wir nicht, da wir dafür keinen gemessenen lokalen Koeffizienten haben; die Unterschiede stehen bei den Verkäufen.",
      rangeNote: "Die Spanne {{min}} – {{max}} ist in der Methodik erläutert.",
      mismatch: "Abweichung:",
      driversTitle: "Was die Schätzung am stärksten bewegt",
      driverSource: "{{label}}: {{pct}} % der Schätzung — {{price}} nach Anpassungen, Beitrag {{eur}}",
      driverStep: "{{step}}: {{eur}} auf die Schätzung",
      unpricedTitle: "Was die Schätzung nicht bewertet",
      unpricedTitleNoEstimate: "Was der Vergleich mit Verkäufen nicht erfasst",
      unpriced: {
        condition: "Zustand und Renovierungen: keine Daten für diese Immobilie oder die Vergleichsverkäufe, daher nicht bewertet. Eine renovierte oder renovierungsbedürftige Immobilie kann erheblich von der Schätzung abweichen.",
        energy: "Energieklasse: im Transaktionsregister keine Angaben zu den Vergleichsverkäufen, daher nicht bewertet.",
        floor: "Stockwerk und Aufzug: nicht im Transaktionsregister, daher nicht bewertet.",
        floor_position: "Stockwerk und Aufzug: für die Vergleichsverkäufe enthält das Transaktionsregister keine Angaben; für diese Immobilie wird der amtliche GURS-Lagefaktor angewendet, der Unterschied zum einzelnen Verkauf aber nicht bewertet.",
        land: "Grundstück: Unterschiede in Größe und Nutzung sind je Verkauf angegeben, aber nicht bewertet.",
        land_unknown: "Grundstück: die Grundstücksfläche dieser Immobilie ist unbekannt, ein Vergleich ist nicht möglich.",
        package_contents: "Inhalt des Geschäfts: bei einigen Verkäufen enthält der Preis Garage, Stellplatz oder Nebengebäude, deren Fläche nicht in den m² steckt; ihr Wert ist nicht abgezogen.",
      },
      nextStepsTitle: "Was als Nächstes zu prüfen ist",
      nextSteps: {
        get_area: "Beschaffen Sie die Netto-Grundfläche der Einheit (GURS-Gebäudekataster, Baupläne oder Verkäufer); ohne sie ist keine Schätzung möglich.",
        appraisal_no_recent_sales: "Für einen aktuellen Wert beauftragen Sie ein Gutachten eines gerichtlich beeideten Sachverständigen: in den letzten 24 Monaten gibt es keinen vergleichbaren realisierten Verkauf, ältere Preise sind nicht der heutige Wert.",
        appraisal_withheld: "Für einen verlässlichen Wert beauftragen Sie ein Gutachten: vergleichbare realisierte Verkäufe sind zu wenige oder streuen zu stark.",
        thin_evidence: "Die Schätzung beruht auf sehr wenigen realisierten Verkäufen; ein untypischer Verkauf kann sie stark verschieben. Holen Sie vor einem verbindlichen Angebot weitere Vergleiche oder ein Gutachten ein.",
        no_recent_sales: "Kein vergleichbarer realisierter Verkauf in den letzten 24 Monaten; für das heutige Preisniveau stützen Sie sich auf ein Gutachten oder geprüfte vereinbarte Preise in der Umgebung.",
        inspect_condition: "Prüfen Sie Zustand, Renovierungen und Energieklasse bei der Besichtigung und im Energieausweis — das sind die wichtigsten Unterschiede, die die Schätzung nicht bewertet.",
        size_spread: "Die meisten gezeigten Verkäufe weichen in der Fläche um 20 % oder mehr ab; der Preis pro m² ist bei kleineren Einheiten meist höher, bei größeren niedriger.",
        house_parcels: "Prüfen Sie, welche Parzellen und Nebengebäude zum Verkauf gehören (Grundbuch, Katasterplan), und vergleichen Sie sie mit den Verkäufen oben.",
        etn_unweighted: "Realisierte Verkäufe haben in dieser Schätzung kein Gewicht (sie beruht auf Angebotspreisen); fragen Sie Verkäufer oder Makler nach erzielten Preisen vergleichbarer Immobilien.",
      },
    },
    actionEyebrow: "Nächste Schritte",
    actionTitle: "Was vor der Unterzeichnung zu prüfen ist",
    actionDesc:
      "Die Reihenfolge ist bewusst gewählt: zuerst, was wir für diese Immobilie nicht prüfen konnten, dann die Standardprüfungen bei jedem Kauf.",
    limitationsTitle: "Einschränkungen dieses Berichts",
    limitationsIntro: "Einige Angaben konnten bei der Erstellung nicht beschafft oder geprüft werden. Unten steht, was fehlt, warum und was Sie tun können. Die übrigen Angaben stammen aus den genannten öffentlichen Registern und Inseraten zum Zeitpunkt der Erstellung; Fehler in den Registern selbst lassen sich nicht ausschließen.",
    limitationsRecovery: "Was Sie tun können:",
    qualityReviewNotice: "Dieser Bericht wird geprüft, weil die automatische Qualitätsprüfung nicht alle Zahlen bestätigen konnte. Bitte verwenden Sie die Zahlen erst nach Abschluss der Prüfung.",
    execTitle: "Zusammenfassung",
    cadastralIdentity: { building: "Gebäude", apartment: "Wohnung", part: "Katastereinheit", parcel: "Parzelle", municipality: "Katastralgemeinde" },
    scopeWholeTitle: "Bewertungsumfang: das gesamte Gebäude",
    scopePartTitle: "Bewertungsumfang: eine einzelne Gebäudeeinheit",
    scopeParts: "Enthaltene erfasste Einheiten",
    scopeAreaParts: "Einheiten in der Bewertungsfläche",
    scopeWholeNote: "Wenn Sie nur einen Teil des Hauses kaufen, verwenden Sie dafür nicht die Schätzung des gesamten Gebäudes. Klären Sie, welche Einheiten verkauft werden.",
    scopePartNote: "Diese Analyse betrifft die genannte Einheit, nicht das gesamte Gebäude. Prüfen Sie, ob dies die Einheit ist, die Sie kaufen.",
    execValue: "Orientierende Wertschätzung",
    execGursOnlyValue: "Orientierung auf GURS-Basis",
    execRangePrefix: "Orientierende Modellspanne",
    execRangeNote: "Die Spanne garantiert nicht, dass der spätere Verkaufspreis innerhalb dieser Grenzen liegt, und ist kein Verhandlungsspielraum.",
    execInterpretations: {
      high: "Vergleichbare abgeschlossene Verkäufe stützen die Schätzung. Sie bestimmt kein sinnvolles Höchstangebot; auch Zustand, Kosten und Ihr Budget sind entscheidend.",
      medium: "Die Schätzung nutzt verfügbare Quellen mit den genannten Einschränkungen. Prüfen Sie vor einem verbindlichen Angebot Vergleichbarkeit, Zustand und Kosten; der Betrag ist keine Angebotsobergrenze.",
      low: "Die Schätzung dient vor allem der Orientierung. Vor einem verbindlichen Angebot empfehlen wir weitere Prüfungen.",
      gurs_only: "Der Betrag beruht ausschließlich auf GURS mit einer angenommenen Modellanpassung. Verwendbare Vergleichsverkäufe oder Angebote fehlen; er dient nur der Orientierung. Lassen Sie vor einem verbindlichen Angebot ein unabhängiges Gutachten erstellen.",
      limits: "Gründe für eingeschränkte Verlässlichkeit:",
    },
    execCeiling: "Gerundeter Orientierungswert",
    execCeilingNote: "Gerundeter Ausgangspunkt dieser Analyse. Die Schätzspanne zeigt Unsicherheit, keinen Verhandlungsspielraum.",
    execTopRisk: "Hauptrisiko",
    execNoRisk: "Keine offenen Hinweise",
    execDisclaimer:
      "Ein informativer Bericht für bessere Entscheidungen — er ersetzt keine amtliche Bewertung.",
    riskEyebrow: "Prüfstatus",
    propertyChecks: propertyChecksDe,
    supportingDocuments: supportingDocumentsDe,
    riskTitle: "Was geprüft wurde und was nicht",
    riskDesc:
      "Diese Analyse beruht auf öffentlichen Registern. Nachfolgend steht ausdrücklich, was wir prüfen konnten, was nicht, und was vor der Unterzeichnung zu prüfen ist.",
    appraisalTitle: "Vor einem verbindlichen Angebot",
    appraisalBeforeOffer: "vor dem Angebot",
    confidenceTitle: "Verlässlichkeit der Schätzung",
    confidenceHigh: "Hoch",
    confidenceMedium: "Mittel",
    confidenceLow: "Gering",
    confidenceHighNote:
      "In der Umgebung gibt es genügend vergleichbare realisierte Verkäufe und Inserate, um die Schätzung solide zu stützen. Käufer haben eine verlässliche Verhandlungsbasis, Verkäufer eine für die Preisfindung.",
    confidenceMediumNote:
      "Die Schätzung nutzt die verfügbaren Quellen, es bleibt jedoch Spielraum für Abweichungen. Verstehen Sie sie als Orientierung statt als exakten Wert und kalkulieren Sie bei Verhandlungen mit einer breiteren Spanne.",
    confidenceLowNote:
      "Die Objekt- oder Marktdaten der Schätzung sind begrenzt; sie dient als Orientierung. Gründe und Datengrundlagen stehen unten. Vor einer wichtigen Entscheidung empfehlen wir weitere Prüfungen oder ein unabhängiges Gutachten.",
    confidenceLimits: {
      few_comparable_sales: "Wenige vergleichbare realisierte Verkäufe",
      wide_comparable_spread: "Große Streuung vergleichbarer Verkaufspreise",
      very_wide_comparable_spread: "Sehr große Streuung vergleichbarer Verkaufspreise",
      stale_comparable_sales: "Ältere Vergleichsverkäufe",
      very_stale_comparable_sales: "Sehr alte Vergleichsverkäufe",
      sources_disagree: "Preisquellen weichen voneinander ab",
      sources_disagree_strongly: "Preisquellen weichen stark voneinander ab",
      house_segment: "Häuser unterscheiden sich stärker in Zustand, Grundstück und Alter",
      subject_area_unconfirmed: "Fläche ist nicht durch mehrere Quellen bestätigt",
      subject_data_limited: "Begrenzte Daten zum Objekt oder zur Bewertungsfläche",
      asking_evidence_weak: "Größeres Inseratsgebiet oder eingeschränkte Vergleichbarkeit",
      gurs_only: "Schätzung beruht nur auf dem GURS-Modellwert",
    },
    confidenceComparables: "Vergleichbare realisierte Verkäufe",
    confidenceListings: "Vergleichbare aktive Inserate",
    confidenceListingQuality: "Qualität der Inseratsdaten",
    confidenceDispersion: "Preisstreuung vergleichbarer Verkäufe",
    confidenceDataAge: "Alter des jüngsten Verkaufs",
    confidenceMonths: "{{count}} Mon.",
    avgPrice: "Durchschnittspreis",
    medianPrice: "Medianpreis",
    minPrice: "Niedrigster Preis",
    maxPrice: "Höchster Preis",
    pricePerM2: "Preis pro m²",
    propertyValue: "Immobilienwert",
    marketAverage: "Angebotspreise, auf diese Fläche umgerechnet",
    askingHaircut: "Angewandter Abschlag vom Angebotspreis",
    differenceFromAsking: "Abweichung von den Angebotspreisen pro m²",
    pricePercentile: "Preisperzentil",
    marketSegment: "Preisgruppe unter Angeboten",
    positionValueLabel: "Verglichene Berechnung",
    marketSegmentLabels: {"budget": "Unteres Viertel", "average": "25. bis 50. Perzentil", "premium": "50. bis 75. Perzentil", "luxury": "Oberes Viertel"},
    avgSize: "Durchschnittsgröße",
    avgYearBuilt: "Durchschnittliches Baujahr",
    newerProperties: "Neuere Immobilien",
    olderProperties: "Ältere Immobilien",
    askingVsSold: "Angebot vs. Verkauf Delta",
    negotiationPotential: "Verhandlungspotenzial",
    negotiationNotAssessed: "Nicht bewertet. Die Differenz zwischen Angebots- und Verkaufspreisen bestimmt keinen Rabatt für diese Immobilie.",
    marketLiquidity: "Marktliquidität",
    gursGeneralized: "GURS-generalisierte Wert",
    landGursValue: "GURS-Grundstückswert",
    analyzedDeals: "Analysierte Geschäfte",
    avgEtnPrice: "Durchschnittspreis",
    priceTrend: "Preistrend",
    closestDeal: "Nächstes Geschäft",
    year: "Jahr",
    transactions: "Transaktionen",
    avgPriceM2: "Durchschn. Preis/m²",
    medianPriceM2: "Median Preis/m²",
    change: "Veränderung",
    overallGrowth: "Gesamtwachstum",
    annualizedGrowth: "Jährliches Wachstum (CAGR)",
    comparableArea: "Vergleichsfläche (netto, Nebenräume gewichtet)",
    comparableAreaShort: "Vergleichsfläche",
    valuedOnArea: "{{price}} × Vergleichsfläche {{area}}",
    comparableAreaFlat: "Vergleichsfläche = (Nutzfläche {{usable}} + {{weight}} × Nebenräume {{ancillary}}) / {{ratio}}{{unallocated}} = {{area}} (Netto-Grundfläche {{net}}).",
    comparableAreaUnallocated: " + nicht zugeordnete Räume {{area}}",
    comparableAreaHouse: "Vergleichsfläche = netto {{net}} − Nebenräume {{ancillary}} + {{weight}} × {{ancillary}} = {{area}}: Terrassen und Keller zählen mit geringerem Gewicht als der durchschnittliche m² vergleichbarer Häuser.",
    comparableAreaHouseNoAncillary: "Die Vergleichsfläche entspricht der Netto-Grundfläche {{net}} (GURS führt keine Terrassen oder Keller auf).",
    comparableAreaWhy: "Die Preise der Vergleichsverkäufe beziehen sich auf den m² Netto-Grundfläche, die auch deren Keller und Balkone umfasst; Nebenräume werden daher auf beiden Seiten gleich gewichtet.",
    growthCaveatNote: "‡ Eine Veränderung von mehr als 25 % in einem Jahr ({{years}}) geht bei dieser lokalen Stichprobe höchstwahrscheinlich auf eine andere Zusammensetzung der verkauften Immobilien zurück (Größe, Alter, Neubauten), nicht auf eine tatsächliche Preisbewegung.",
    anomalyNote:
      "* Ein Jahr mit sehr wenigen Abschlüssen und einem Preis, der stark vom lokalen Durchschnitt abweicht — meist ein einzelner untypischer Verkauf (Anteilsverkauf, Übertragung zwischen verbundenen Personen oder eine Immobilie anderer Kategorie). Wir zeigen ihn weiterhin an, lassen ihn aber aus den Wachstumszahlen heraus.",
    address: "Adresse",
    price: "Preis",
    distance: "Entfernung",
    dealDate: "Geschäftsdatum",
    indexedPricePerM2: "Zum Bewertungsdatum",
    indexedPriceNote:
      "Zum Bewertungsdatum {{date}}: erzielter Preis, umgerechnet auf diesen Tag mit der geschätzten lokalen jährlichen Veränderung von {{rate}}. Ältere Rohpreise sind nicht direkt mit den Angebotspreisen aus dem Berichtszeitraum vergleichbar. Verkäufe, die am Bewertungsdatum älter als fünf Jahre sind, werden nicht indexiert. † kennzeichnet einen älteren Verkauf, der nicht in die Schätzung eingeht.",
    adDescription: "Beschreibung",
    adPriceLevel: "Preisniveau",
    adPriceBelow: "Unter Angebotsniveau",
    adPriceWithin: "Auf Angebotsniveau",
    adPriceAbove: "Über Angebotsniveau",
    adLocation: "Standort",
    link: "Link",
    openAd: "Inserat öffnen",
    na: "k. A.",
    paginationPrevious: "Zurück",
    paginationNext: "Weiter",
    paginationShowing: "Anzeige {{from}}–{{to}} von {{total}}",
    filtersTitle: "Filter",
    filterMin: "Min",
    filterMax: "Max",
    filtersClear: "Zurücksetzen",
    mapSubject: "Bewertete Immobilie",
    mapComparablesTitle: "Vergleichbare Verkäufe auf der Karte",
    share: {
      button: "Teilen",
      title: "Bericht teilen",
      description: "Jeder mit diesem Link kann den Bericht ansehen. Keine Anmeldung nötig.",
      notCompleted: "Sie können den Bericht teilen, sobald er fertig ist.",
      generate: "Link erstellen",
      generating: "Wird erstellt…",
      linkLabel: "Öffentlicher Link",
      copy: "Kopieren",
      copied: "Kopiert!",
      revoke: "Zugriff widerrufen",
      revoking: "Wird widerrufen…",
      notShared: "Dieser Bericht wurde noch nicht geteilt.",
      error: "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.",
    },
    sharedCta: "Erstellen Sie Ihren eigenen Bericht",
    sharedClosingTitle: "Prüfen Sie eine weitere Immobilie",
    sharedClosingText:
      "Diesen Bericht hat jemand für eine bestimmte Adresse erstellt. Sie können dieselbe Prüfung für jede Adresse in Slowenien durchführen — der erste Bericht ist kostenlos.",
    sharedClosingCta: "Ihre Immobilie prüfen",
    sharedUnavailableTitle: "Bericht nicht verfügbar",
    sharedUnavailableText: "Dieser Berichtslink ist nicht mehr verfügbar.",
    intel: {
      eyebrow: "Lokaler Angebotsmarkt",
      title: "Marktintelligenz der Umgebung",
      desc: "Analyse aktiver Inserate vergleichbarer Immobilien in der Umgebung (nepremicnine.net).",
      sampleNote: "Basierend auf {{count}} aktiven Inseraten ({{locality}}).",
      sampleNoteAsOf: "Basierend auf {{count}} aktiven Inseraten ({{locality}}), Stand der letzten Erfassung am {{date}}.",
      medianAsking: "Median-Angebotspreis",
      askingRange: "Spanne (P25–P75)",
      yourValue: "Ihr geschätzter Wert",
      percentile: "Position unter den Inseraten",
      pricingBelow: "Ihr Wert pro m² liegt unter den meisten lokalen Angebotspreisen.",
      pricingInline: "Ihr Wert entspricht den lokalen Angebotspreisen.",
      pricingAbove: "Ihr Wert pro m² liegt über den meisten lokalen Angebotspreisen.",
      newBuildShare: "Anteil Neubauten",
      priceDrops: "Preissenkungen",
      dropsDetail: "{{share}} der Inserate, Ø {{avg}}",
      observedAge: "Beobachtetes Inseratsalter (Median)",
      observedAgeValue: "{{days}} Tage",
      observedAgeLowerBound: "mindestens {{days}} Tage",
      delisted: "Entfernte Inserate (letzte 30 beobachtete Tage)",
      delistedDetail: "{{delisted}} entfernt / {{active}} aktiv",
      activityOutage: "Die Erfassung der Inserate ist seit {{date}} unterbrochen, daher zeigen wir weder Inseratsalter noch Entfernungen; die Angebotspreise entsprechen der letzten Erfassung.",
      activityShallow: "Das Inseratsarchiv hat noch keine ausreichend lange lückenlose Historie, daher zeigen wir weder Inseratsalter noch Entfernungen.",
      activityNote: "Das beobachtete Alter ist die Zeit zwischen unserer ersten und letzten Erfassung eines Inserats, nicht das ursprüngliche Veröffentlichungsdatum. Ein entferntes Inserat ist kein Verkauf. Die Verkaufsdauer schätzen wir nicht, da Inserate nicht mit erfassten Verkäufen verknüpft werden können.",
    },
  },
  creditHistory: {
    title: "Credit-Verlauf",
    filterPlaceholder: "Nach Typ filtern",
    filterAll: "Alle Transaktionen",
    filterPurchase: "Käufe",
    filterUsage: "Nutzung",
    filterExpiring: "Bald ablaufend",
    date: "Datum",
    noResults: "Keine Ergebnisse.",
    noResultsHint: "Versuchen Sie andere Filter.",
    noTransactions: "Keine Transaktionen.",
    noTransactionsHint: "Ihre Transaktionen erscheinen hier.",
    expired: "Abgelaufen",
    expiresIn: "Läuft in {{days}} Tagen ab",
    validUntil: "Gültig bis",
  },
  creditBalance: {
    title: "Meine Credits",
    available: "Verfügbare Credits",
    refresh: "Saldo aktualisieren",
  },
  embeddedCheckout: {
    loginRequired: "Sie müssen angemeldet sein, um zu bezahlen.",
    createError: "Fehler beim Erstellen der Zahlung. Bitte versuchen Sie es erneut.",
    preparing: "Zahlung wird vorbereitet...",
    tryAgain: "Erneut versuchen",
    successTitle: "Zahlung erfolgreich!",
    successDesc: "Credits wurden Ihrem Konto gutgeschrieben.",
  },
  checkoutModal: {
    eyebrow: "Zahlung",
    purchase: "Kauf: {{name}}",
    purchaseCredits: "{{count}} Credits kaufen",
  },
  ratePage: {
    backHome: "Zurück zur Startseite",
  },
  userRating: {
    title: "Wie würden Sie unseren Service bewerten?",
    subtitle: "Ihr Feedback hilft uns, uns zu verbessern",
    rateAria: "{{star}} von 5 Sternen bewerten",
    saveError: "Ihre Bewertung konnte nicht gespeichert werden.",
    thanksToast: "Vielen Dank!",
    thanksToastDesc: "Ihre Bewertung wurde erfolgreich gespeichert.",
    thanks: "Vielen Dank für Ihre Bewertung!",
  },
  cookieConsent: {
    title: "Wir respektieren Ihre Privatsphäre",
    description:
      "Unbedingt erforderliche Cookies nutzen wir für den Betrieb der Website. Analyse- und Marketing-Cookies laden wir nur mit Ihrer Einwilligung. Ihre Auswahl können Sie jederzeit ändern.",
    cookiePolicyLink: "Cookie-Richtlinie",
    privacyPolicyLink: "Datenschutzerklärung",
    necessaryTitle: "Unbedingt erforderlich",
    necessaryDesc: "Anmeldung, Sicherheit und Zahlung. Diese lassen sich nicht abschalten.",
    analyticsTitle: "Analyse",
    analyticsDesc: "Messung von Reichweite und Nutzung, damit wir den Dienst verbessern können.",
    marketingTitle: "Marketing",
    marketingDesc: "Messung der Werbewirkung und Ausspielung relevanterer Anzeigen.",
    acceptAll: "Alle akzeptieren",
    rejectAll: "Nicht notwendige ablehnen",
    customize: "Einstellungen",
    saveSelection: "Auswahl speichern",
  },
  withdrawal: {
    heading: "Vor der Zahlung: sofortige Ausführung",
    explainer:
      "Ein Bericht ist ein digitaler Inhalt, den wir unmittelbar nach der Zahlung erstellen. Das slowenische Verbraucherschutzgesetz (Artikel 135 ZVPot-1) verlangt, dass Sie uns ausdrücklich zum sofortigen Beginn ermächtigen und zur Kenntnis nehmen, dass Sie dadurch für den erstellten Bericht Ihr 14-tägiges Widerrufsrecht verlieren. Nicht verbrauchte Credits bleiben 14 Tage erstattungsfähig.",
    consentLabel:
      "Ich stimme zu, dass die Ausführung sofort beginnt, und nehme zur Kenntnis, dass ich mit vollständiger Ausführung mein Widerrufsrecht für den erstellten Bericht verliere.",
    termsPrefix: "Mit dem Fortfahren akzeptieren Sie:",
  },
  legal: legalDe,
};
